import { DateTime } from 'luxon';
import { GetActivityPageSummaryModeEnum, GetActivityPageSummaryPeriodEnum, QueryActivityPageMetrics, QueryActivityPagePeriod } from '../../../api/schema';

export type ActivityPeriod = GetActivityPageSummaryPeriodEnum;
export type ActivityMode = GetActivityPageSummaryModeEnum;
export type ActivityGrain = 'day' | 'week' | 'month';

export const ACTIVITY_PERIODS: ActivityPeriod[] = ['week', 'quarter', 'year'];
export const ACTIVITY_MODES: ActivityMode[] = ['trailing', 'to_date'];

export const PERIOD_LABELS: Record<ActivityPeriod, string> = {
  week: 'Week',
  quarter: 'Quarter',
  year: 'Year',
};

export const MODE_LABELS: Record<ActivityMode, string> = {
  trailing: 'Trailing',
  to_date: 'To Date',
};

/** Sub-period a period is broken into, mirroring the server's own mapping. */
export const PERIOD_GRAIN: Record<ActivityPeriod, ActivityGrain> = {
  week: 'day',
  quarter: 'week',
  year: 'month',
};

/**
 * How many sub-periods a window has, used only to size the loading skeleton
 * before the first response. The real list comes from the server, which owns
 * every calendar boundary so the two never disagree about where a week starts
 * or what a trailing quarter covers.
 */
export const SKELETON_ROWS: Record<ActivityPeriod, number> = { week: 7, quarter: 13, year: 12 };

export const parsePeriod = (value?: string): ActivityPeriod => (ACTIVITY_PERIODS.includes(value as ActivityPeriod) ? (value as ActivityPeriod) : 'week');

export const parseMode = (value?: string): ActivityMode => (ACTIVITY_MODES.includes(value as ActivityMode) ? (value as ActivityMode) : 'trailing');

export const parseGrain = (value?: string): ActivityGrain => (value === 'week' || value === 'month' ? value : 'day');

/** Metrics with every field present, so a missing row reads as a quiet period. */
export const ZERO_METRICS: Required<QueryActivityPageMetrics> = {
  jobs_opened: 0,
  jobs_closed: 0,
  jobs_expanded: 0,
  sites_active: 0,
  equipment_out: 0,
  equipment_in: 0,
  boomerang_real: 0,
  boomerang_artifact: 0,
};

export const metricsOf = (row?: QueryActivityPageMetrics): Required<QueryActivityPageMetrics> => ({
  ...ZERO_METRICS,
  ...(row || {}),
});

/**
 * How a sub-period is named in the list and on its own page. A clipped period
 * says so, because a three-day "week" that reads as a whole one makes the
 * per-day rates look wrong.
 */
export const periodLabel = (row: QueryActivityPagePeriod, grain: ActivityGrain, tz: string): string => {
  const start = DateTime.fromISO(row.date || '', { zone: tz });
  if (!start.isValid) return row.date || '';
  if (grain === 'day') return start.toFormat('EEE, MMM d');
  if (grain === 'month') return start.toFormat('MMMM yyyy');

  const from = DateTime.fromISO(row.from || '', { zone: tz });
  const clipped = from.isValid && from > start.startOf('day');
  return clipped ? `Week of ${start.toFormat('MMM d')} (from ${from.toFormat('MMM d')})` : `Week of ${start.toFormat('MMM d')}`;
};

/** The heading of a drill-down page. */
export const detailLabel = (from: string, to: string, grain: ActivityGrain, tz: string): string => {
  const start = DateTime.fromISO(from, { zone: tz });
  if (!start.isValid) return '';
  if (grain === 'day') return start.toFormat('EEEE, MMM d yyyy');
  if (grain === 'month') return start.toFormat('MMMM yyyy');
  const end = DateTime.fromISO(to, { zone: tz });
  return end.isValid ? `${start.toFormat('MMM d')} – ${end.minus({ days: 1 }).toFormat('MMM d, yyyy')}` : start.toFormat('MMM d');
};

/** "Sep 14 – Sep 20, 2026 · 7 days", the caption beside the page controls. */
export const windowCaption = (from?: string, to?: string, days?: number, tz?: string): string => {
  const start = DateTime.fromISO(from || '', { zone: tz });
  const end = DateTime.fromISO(to || '', { zone: tz });
  if (!start.isValid || !end.isValid) return '';
  const span = `${start.toFormat('MMM d')} – ${end.toFormat('MMM d, yyyy')}`;
  return days === undefined ? span : `${span} · ${formatDays(days)}`;
};

export const formatDays = (days: number): string => {
  const rounded = Math.round(days * 10) / 10;
  const label = Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
  return `${label} ${rounded === 1 ? 'day' : 'days'}`;
};

// There is deliberately no per-day helper here. Dividing a period's count by
// the days elapsed in it projects one stretch of work across a whole day, which
// only holds if the work runs evenly from midnight to midnight -- it never
// does. Every number on these pages is the count that actually happened; a
// partial period simply shows a smaller one, which is the truth about a period
// that has not finished.

export const formatRate = (value: number): string => (Number.isInteger(value) ? `${value}` : value.toFixed(1));

/** Drill-down URL for one sub-period, carrying the server's own bounds. */
export const detailPath = (row: QueryActivityPagePeriod, grain: ActivityGrain, period: ActivityPeriod, mode: ActivityMode): string => {
  const params = new URLSearchParams({
    from: row.from || '',
    to: row.to || '',
    period,
    mode,
  });
  return `/activity/dashboard/activity/${grain}/${row.date}?${params.toString()}`;
};
